import {Observable, of, pipe} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage,
    ArrayEncodedMdUpdateMessage
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {Quote} from './quote';
import {QuotesSubscriptionSubject} from './quotes-subscription-subject';

enum PrivateQuotesWebSocketMessageType {
    PRIVATE_QUOTES_SNAPSHOT = 'PRIVATE_QUOTES_SNAPSHOT',
    PRIVATE_QUOTES_UPDATE   = 'PRIVATE_QUOTES_UPDATE'
}

interface SmartQuotesWebSocketMessage extends WebSocketMessage {
    messageType: PrivateQuotesWebSocketMessageType;
    data: ArrayEncodedMdMessage[];
}

interface PrivateQuotesSnapshotWebSocketMessage extends SmartQuotesWebSocketMessage {
    messageType: PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_SNAPSHOT;
    data: ArrayEncodedMdSnapshotMessage[];
}

interface PrivateQuotesUpdateWebSocketMessage extends SmartQuotesWebSocketMessage {
    messageType: PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_UPDATE;
    data: ArrayEncodedMdUpdateMessage[];
}

export class PrivateQuotesSubscriptionSubject extends QuotesSubscriptionSubject {

    private privateAggregatedMessageTypeToCommonDict: {
        [aggregatedMessageType: string]: PrivateQuotesWebSocketMessageType
    };

    protected override createSubjectType(): void {
        this.type = {
            name: `PRIVATE_QUOTES${this.aggregationSuffix}`,
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: `SUBSCRIBE_PRIVATE_QUOTES${this.aggregationSuffix}`,
                UNSUBSCRIBE: `UNSUBSCRIBE_PRIVATE_QUOTES${this.aggregationSuffix}`
            },
            messagePipe: pipe(
                filter((message: WebSocketMessage) =>
                    !!this.privateAggregatedMessageTypeToCommonDict[message.messageType]),

                mergeMap((message: WebSocketMessage): Observable<Quote> => {
                    const quotes: Quote[] = this.readPrivaeQuotesMessage(message);

                    return of(...quotes);
                })
            ),
            subjectPipeFactory: subject => this.createSubjectPipe(subject),
            deprecateSubject: subject => this.deprecateSubject(subject)
        };
    }

    protected override createWebSocketMessageTypes(): void {
        const snapshotMessage = [PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_SNAPSHOT, this.aggregationSuffix]
            .join('');
        const updateMessage = [PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_UPDATE, this.aggregationSuffix]
            .join('');

        this.privateAggregatedMessageTypeToCommonDict = {
            [snapshotMessage]: PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_SNAPSHOT,
            [updateMessage]:   PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_UPDATE
        };

        // Support old format of answers without #1
        if (this.aggregationScale === 1 && this.aggregationSuffix) {
            this.privateAggregatedMessageTypeToCommonDict[PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_SNAPSHOT] =
                PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_SNAPSHOT;
            this.privateAggregatedMessageTypeToCommonDict[PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_UPDATE] =
                PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_UPDATE;
        }
    }

    private readPrivaeQuotesMessage(message: SmartQuotesWebSocketMessage): Quote[] {
        let quotes: Quote[];
        const msgType = this.privateAggregatedMessageTypeToCommonDict[message.messageType];

        switch (msgType) {

            case PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_SNAPSHOT:
                quotes = this.readQuotesSnapshot(message.data as ArrayEncodedMdSnapshotMessage[]);
                break;

            case PrivateQuotesWebSocketMessageType.PRIVATE_QUOTES_UPDATE:
                quotes = this.readQuotesUpdate(message.data as ArrayEncodedMdUpdateMessage[]);
                break;

            default:
                quotes = [];
                break;
        }

        return quotes;
    }

}
